import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import {
  ShoppingBagIcon,
  CreditCardIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  FileTextIcon,
  PhoneIcon,
  BellIcon,
  ZapIcon,
  CheckCircleIcon,
  RefreshIcon
} from "../components/Icons";

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "Just now";
  const date = new Date(dateStr);
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function Notifications({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [actionNotice, setActionNotice] = useState(null);

  const fetchNotifications = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const res = await api.get("/vendor/notifications");
      if (res.data && res.data.success) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.error("Failed to load vendor notifications:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Background polling every 45s for fresh orders & warnings
    const interval = setInterval(() => {
      fetchNotifications(false);
    }, 45000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.put(`/vendor/notifications/${id}/read`);
      if (res.data && res.data.success) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, notifactionRead: true } : n))
        );
        setUnreadCount(res.data.unreadCount ?? Math.max(0, unreadCount - 1));
      }
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/vendor/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, notifactionRead: true })));
      setUnreadCount(0);
      setActionNotice({ type: "success", text: "All notifications marked as read." });
      setTimeout(() => setActionNotice(null), 3500);
    } catch (err) {
      console.error("Failed to mark all notifications read:", err);
    }
  };

  const handleRunHealthAudit = async () => {
    try {
      setScanning(true);
      await api.post("/vendor/notifications/run-automation-checks");
      await fetchNotifications(false);
      setActionNotice({
        type: "success",
        text: `Store audit complete! Checked stagnant orders, stock alerts, subscription validity, and KYC documents.`,
      });
      setTimeout(() => setActionNotice(null), 5000);
    } catch (err) {
      console.error("Audit scan failed:", err);
      setActionNotice({ type: "error", text: "Audit scan failed to complete." });
      setTimeout(() => setActionNotice(null), 4000);
    } finally {
      setScanning(false);
    }
  };

  // Classify notification type & color badge
  const getBadgeDetails = (notif) => {
    const type = notif.type || "general";
    if (type.includes("order") || type === "vendor_new_order" || type === "order_placed") {
      return { label: "New Order", icon: <ShoppingBagIcon size={14} color="#166534" />, color: "#166534", bg: "#dcfce7", border: "#bbf7d0" };
    }
    if (type.includes("low_stock") || type.includes("inventory")) {
      return { label: "Low Inventory", icon: <AlertTriangleIcon size={14} color="#c2410c" />, color: "#c2410c", bg: "#ffedd5", border: "#fed7aa" };
    }
    if (type.includes("sub_expiring") || type.includes("subscription_expiring")) {
      return { label: "Recharge Due", icon: <CreditCardIcon size={14} color="#b45309" />, color: "#b45309", bg: "#fef3c7", border: "#fde68a" };
    }
    if (type.includes("sub_expired") || type.includes("subscription_expired")) {
      return { label: "Plan Expired", icon: <AlertCircleIcon size={14} color="#b91c1c" />, color: "#b91c1c", bg: "#fee2e2", border: "#fca5a5" };
    }
    if (type.includes("stagnant") || type.includes("delay")) {
      return { label: "24h Order Delay", icon: <AlertTriangleIcon size={14} color="#991b1b" />, color: "#991b1b", bg: "#fee2e2", border: "#f87171" };
    }
    if (type.includes("surge") || type.includes("high_orders")) {
      return { label: "Order Surge", icon: <ZapIcon size={14} color="#15803d" />, color: "#15803d", bg: "#dcfce7", border: "#86efac" };
    }
    if (type.includes("admin_warning") || type.includes("blocked") || type.includes("warning")) {
      return { label: "Admin Notice", icon: <AlertTriangleIcon size={14} color="#991b1b" />, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" };
    }
    if (type.includes("inactive")) {
      return { label: "Inactivity Alert", icon: <AlertCircleIcon size={14} color="#475569" />, color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" };
    }
    if (type.includes("doc") || type.includes("kyc")) {
      return { label: "Incomplete KYC", icon: <FileTextIcon size={14} color="#6b21a8" />, color: "#6b21a8", bg: "#f3e8ff", border: "#d8b4fe" };
    }
    if (type.includes("review") || type.includes("rating")) {
      return { label: "Product Review", icon: <ShoppingBagIcon size={14} color="#a16207" />, color: "#a16207", bg: "#fef9c3", border: "#fef08a" };
    }
    if (type.includes("interaction") || type.includes("call") || type.includes("booking")) {
      return { label: "Customer Lead", icon: <PhoneIcon size={14} color="#1d4ed8" />, color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" };
    }
    return { label: "Notification", icon: <BellIcon size={14} color="#334155" />, color: "#334155", bg: "#f1f5f9", border: "#e2e8f0" };
  };

  // Filter list
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notif) => {
      // Tab filter
      if (activeTab === "UNREAD" && notif.notifactionRead) return false;
      if (activeTab === "ORDERS" && !(notif.type?.includes("order") || notif.type?.includes("booking"))) return false;
      if (activeTab === "INVENTORY" && !(notif.type?.includes("stock") || notif.type?.includes("inventory"))) return false;
      if (activeTab === "SUBSCRIPTION" && !(notif.type?.includes("sub") || notif.type?.includes("plan"))) return false;
      if (activeTab === "WARNINGS" && !(notif.type?.includes("warning") || notif.type?.includes("blocked") || notif.type?.includes("doc") || notif.type?.includes("inactive") || notif.type?.includes("stagnant"))) return false;
      if (activeTab === "LEADS" && !(notif.type?.includes("interaction") || notif.type?.includes("call") || notif.type?.includes("review"))) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const titleMatch = notif.title?.toLowerCase().includes(query);
        const msgMatch = notif.message?.toLowerCase().includes(query);
        if (!titleMatch && !msgMatch) return false;
      }
      return true;
    });
  }, [notifications, activeTab, searchQuery]);

  // Aggregate stats
  const stats = useMemo(() => {
    let orders = 0;
    let inventory = 0;
    let subscription = 0;
    let warnings = 0;
    for (const n of notifications) {
      const t = n.type || "";
      if (t.includes("order") || t.includes("booking")) orders++;
      if (t.includes("stock") || t.includes("inventory")) inventory++;
      if (t.includes("sub") || t.includes("plan")) subscription++;
      if (t.includes("warning") || t.includes("blocked") || t.includes("doc") || t.includes("inactive") || t.includes("stagnant")) warnings++;
    }
    return { orders, inventory, subscription, warnings };
  }, [notifications]);

  return (
    <div style={{ padding: "4px 0 50px 0", maxWidth: "1200px", margin: "0 auto", boxSizing: "border-box" }}>
      {/* ═══════════ TOP HEADER & QUICK ACTIONS ═══════════ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "14px",
          marginBottom: "20px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#0f172a", margin: 0 }}>
              Seller Notifications &amp; Alerts
            </h1>
            {unreadCount > 0 ? (
              <span
                style={{
                  background: "#ef4444",
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  boxShadow: "0 2px 6px rgba(239,68,68,0.3)",
                }}
              >
                {unreadCount} Unread
              </span>
            ) : (
              <span
                style={{
                  background: "#10b981",
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "3px 10px",
                  borderRadius: "20px",
                }}
              >
                All Caught Up
              </span>
            )}
          </div>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>
            Real-time updates for customer orders, stock drops, customer calls, subscription dates, and admin messages.
          </p>
        </div>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", width: "100%", maxWidth: "520px" }}>
          <button
            onClick={handleRunHealthAudit}
            disabled={scanning}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#0f172a",
              color: "#ffffff",
              border: "none",
              padding: "9px 16px",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: scanning ? "not-allowed" : "pointer",
              boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
              opacity: scanning ? 0.7 : 1,
              flex: "1 1 auto",
              justifyContent: "center"
            }}
          >
            <ZapIcon size={15} color="#ffffff" />
            <span>{scanning ? "Auditing..." : "Scan Health"}</span>
          </button>

          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#ffffff",
                color: "#1e293b",
                border: "1px solid #cbd5e1",
                padding: "9px 14px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
                flex: "1 1 auto",
                justifyContent: "center"
              }}
            >
              <CheckCircleIcon size={15} color="#166534" />
              <span>Mark All Read</span>
            </button>
          )}

          <button
            onClick={() => fetchNotifications(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#f1f5f9",
              color: "#475569",
              border: "1px solid #e2e8f0",
              padding: "9px 14px",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: "pointer",
              flex: "0 0 auto",
              justifyContent: "center"
            }}
          >
            <RefreshIcon size={14} color="#475569" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* FEEDBACK BANNER */}
      {actionNotice && (
        <div
          style={{
            background: actionNotice.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${actionNotice.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: actionNotice.type === "success" ? "#166534" : "#991b1b",
            padding: "12px 18px",
            borderRadius: "8px",
            fontSize: "13.5px",
            fontWeight: "500",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{actionNotice.text}</span>
          <button
            onClick={() => setActionNotice(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: "700" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ═══════════ METRIC OVERVIEW CARDS ═══════════ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <div
          onClick={() => setActiveTab("ORDERS")}
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: activeTab === "ORDERS" ? "0 0 0 2px #3b82f6" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Orders</span>
            <ShoppingBagIcon size={18} color="#3b82f6" />
          </div>
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#0f172a", marginTop: "4px" }}>{stats.orders}</div>
          <div style={{ fontSize: "11px", color: "#3b82f6", marginTop: "2px" }}>Delivery &amp; bookings</div>
        </div>

        <div
          onClick={() => setActiveTab("INVENTORY")}
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: activeTab === "INVENTORY" ? "0 0 0 2px #f97316" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Low Stock</span>
            <AlertTriangleIcon size={18} color="#f97316" />
          </div>
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#c2410c", marginTop: "4px" }}>{stats.inventory}</div>
          <div style={{ fontSize: "11px", color: "#ea580c", marginTop: "2px" }}>≤ 5 units stock</div>
        </div>

        <div
          onClick={() => setActiveTab("SUBSCRIPTION")}
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: activeTab === "SUBSCRIPTION" ? "0 0 0 2px #eab308" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Plan</span>
            <CreditCardIcon size={18} color="#eab308" />
          </div>
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#a16207", marginTop: "4px" }}>{stats.subscription}</div>
          <div style={{ fontSize: "11px", color: "#ca8a04", marginTop: "2px" }}>Recharges &amp; validity</div>
        </div>

        <div
          onClick={() => setActiveTab("WARNINGS")}
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "14px",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: activeTab === "WARNINGS" ? "0 0 0 2px #ef4444" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Warnings</span>
            <AlertCircleIcon size={18} color="#ef4444" />
          </div>
          <div style={{ fontSize: "22px", fontWeight: "800", color: "#b91c1c", marginTop: "4px" }}>{stats.warnings}</div>
          <div style={{ fontSize: "11px", color: "#dc2626", marginTop: "2px" }}>Admin &amp; KYC alerts</div>
        </div>
      </div>

      {/* ═══════════ SEARCH & FILTER TABS ═══════════ */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          padding: "16px 20px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "14px",
        }}
      >
        {/* TAB BUTTONS */}
        <div style={{ display: "flex", gap: "8px", overflowX: "auto", maxWidth: "100%", paddingBottom: "4px" }}>
          {[
            { id: "ALL", label: `All (${notifications.length})` },
            { id: "UNREAD", label: `Unread (${unreadCount})` },
            { id: "ORDERS", label: `Orders (${stats.orders})` },
            { id: "INVENTORY", label: `Inventory (${stats.inventory})` },
            { id: "SUBSCRIPTION", label: `Subscription (${stats.subscription})` },
            { id: "WARNINGS", label: `Warnings & KYC (${stats.warnings})` },
            { id: "LEADS", label: "Leads & Calls" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: activeTab === tab.id ? "#0f172a" : "#f8fafc",
                color: activeTab === tab.id ? "#ffffff" : "#475569",
                border: activeTab === tab.id ? "1px solid #0f172a" : "1px solid #e2e8f0",
                padding: "8px 14px",
                borderRadius: "8px",
                fontSize: "12.5px",
                fontWeight: activeTab === tab.id ? "700" : "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* SEARCH BOX */}
        <div style={{ minWidth: "220px", flex: 1, maxWidth: "340px" }}>
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              fontSize: "13px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {/* ═══════════ NOTIFICATIONS LIST ═══════════ */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <div className="spinner-border text-brand-primary" role="status" style={{ width: "32px", height: "32px" }}>
            <span className="visually-hidden">Loading notifications...</span>
          </div>
          <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>Fetching seller updates...</p>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: "42px", marginBottom: "12px" }}>📭</div>
          <h3 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700" }}>
            No Notifications Found
          </h3>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
            {searchQuery
              ? `No notifications matching "${searchQuery}".`
              : activeTab === "UNREAD"
              ? "Great job! You have cleared all pending alerts."
              : "You do not have any notifications in this section."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {filteredNotifications.map((notif) => {
            const badge = getBadgeDetails(notif);
            const isUnread = !notif.notifactionRead;
            const actionLink = notif.actionLink || "/vendor/dashboard";

            return (
              <div
                key={notif._id}
                style={{
                  background: isUnread ? "#ffffff" : "#fafafa",
                  borderRadius: "12px",
                  border: isUnread ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
                  padding: "18px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  boxShadow: isUnread ? "0 3px 12px rgba(15,23,42,0.06)" : "none",
                  borderLeft: isUnread ? "4px solid #3b82f6" : "4px solid transparent",
                  transition: "all 0.15s ease",
                }}
              >
                {/* ROW 1: HEADER & STATUS */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    {/* Badge */}
                    <span
                      style={{
                        background: badge.bg,
                        color: badge.color,
                        border: `1px solid ${badge.border}`,
                        fontSize: "11px",
                        fontWeight: "700",
                        padding: "3px 9px",
                        borderRadius: "6px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>{badge.icon}</span>
                      <span>{badge.label}</span>
                    </span>

                    {/* Notification Title */}
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: isUnread ? "800" : "600",
                        color: "#0f172a",
                      }}
                    >
                      {notif.title || "Notification"}
                    </h3>

                    {isUnread && (
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "#3b82f6",
                          display: "inline-block",
                        }}
                        title="Unread"
                      />
                    )}
                  </div>

                  {/* Timestamp */}
                  <span style={{ fontSize: "12px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {formatTimeAgo(notif.createdAt)}
                  </span>
                </div>

                {/* ROW 2: MESSAGE CONTENT */}
                <p
                  style={{
                    margin: 0,
                    fontSize: "13.5px",
                    lineHeight: "1.6",
                    color: isUnread ? "#1e293b" : "#475569",
                  }}
                >
                  {notif.message}
                </p>

                {/* ROW 3: METADATA CHIPS (IF PRESENT) */}
                {notif.metadata && Object.keys(notif.metadata).length > 0 && (
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "2px" }}>
                    {notif.metadata.orderId && (
                      <span style={{ fontSize: "11px", background: "#f1f5f9", padding: "2px 8px", borderRadius: "4px", color: "#475569" }}>
                        <strong>Order:</strong> #{String(notif.metadata.orderId).slice(-6)}
                      </span>
                    )}
                    {notif.metadata.currentStock !== undefined && (
                      <span style={{ fontSize: "11px", background: "#fef3c7", padding: "2px 8px", borderRadius: "4px", color: "#92400e" }}>
                        <strong>Stock Left:</strong> {notif.metadata.currentStock} units
                      </span>
                    )}
                    {notif.metadata.daysLeft !== undefined && (
                      <span style={{ fontSize: "11px", background: "#fee2e2", padding: "2px 8px", borderRadius: "4px", color: "#991b1b" }}>
                        <strong>Expires In:</strong> {notif.metadata.daysLeft} days
                      </span>
                    )}
                    {notif.metadata.customerPhone && (
                      <span style={{ fontSize: "11px", background: "#eff6ff", padding: "2px 8px", borderRadius: "4px", color: "#1d4ed8" }}>
                        <strong>Customer Phone:</strong> +91 {notif.metadata.customerPhone}
                      </span>
                    )}
                  </div>
                )}

                {/* ROW 4: ACTION BUTTONS */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "6px",
                    paddingTop: "8px",
                    borderTop: "1px solid #f1f5f9",
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    to={actionLink}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      background: "#0f172a",
                      color: "#ffffff",
                      fontSize: "12.5px",
                      fontWeight: "700",
                      padding: "7px 14px",
                      borderRadius: "6px",
                      textDecoration: "none",
                    }}
                  >
                    Take Action &rarr;
                  </Link>

                  {isUnread && (
                    <button
                      onClick={(e) => handleMarkAsRead(notif._id, e)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
